/**
 * create-board/model — types, static choice data, and the payload helper for
 * CreateBoardDialog. Extracted VERBATIM from the former monolith during the
 * <500-line decomposition; re-exported by the orchestrator so existing
 * importers keep their `from './CreateBoardDialog'` paths.
 */

import type { Board, BoardKind, BoardVisibility } from '../../types'
import type { KanbanMember } from '../../api'
import type { InvitedMember } from '../BoardInvitePicker'
// Slug derivation lives in `../../utils/boardSlug` so the duplicate flow and
// the template-clone flow share the same logic instead of each surface
// re-inventing it.
import { buildUniqueBoardSlug } from '../../utils/boardSlug'

export interface CreateBoardDialogProps {
  open: boolean
  onClose: () => void
  /** Org id to scope the board to. Required by the backend. */
  organisationId: number
  /**
   * Called with the validated form. Should POST to /boards/ and
   * resolve to the created board's id (used by the parent to
   * navigate the user into the new board on success). When the
   * input has a `cloneFromBoardId`, the parent is expected to ALSO
   * copy the lanes/theme from the template board after creating
   * the new one.
   */
  onCreate: (input: CreateBoardInput) => Promise<{ id: string }>
  /** Limit which kinds the dialog offers — e.g. omit AR_AP for
   *  tenants that don't have a finance integration. Defaults to the
   *  Trello-overlap subset. */
  kindChoices?: Array<{ value: BoardKind; label: string; description: string }>
  /** When provided, enables the "Clone from board…" picker. Should
   *  resolve into the org's boards (callers usually pass the same
   *  list they show in the left rail). When omitted, the picker
   *  is hidden. */
  loadTemplateCandidates?: () => Promise<Board[]>
  /** Org roster for the "invite specific members" picker that appears
   *  when visibility is "Members only". When omitted (or empty) the
   *  invite picker is hidden and the dialog behaves as before — the
   *  operator can still invite people later via the board's Access
   *  popover. */
  orgMembers?: KanbanMember[]
}

export interface CreateBoardInput {
  name: string
  kind: BoardKind
  visibility: BoardVisibility
  description?: string
  organisation: number
  /** When set, the parent should copy this board's lanes + theme
   *  into the new board after createBoard resolves. */
  cloneFromBoardId?: string
  /** Whether to also copy `config_json` (theme + background image)
   *  from the template board. Defaults to true when cloning. */
  cloneConfig?: boolean
  /** Teammates to grant access to after the board is created (only set
   *  when visibility is "Members only"). The parent grants each via
   *  `api.grantBoardMember(boardId, userId, role)` once createBoard
   *  resolves. The creator is the OWNER automatically (server-side). */
  invitedMembers?: InvitedMember[]
}

export const DEFAULT_KIND_CHOICES: Array<{
  value: BoardKind
  label: string
  description: string
}> = [
  { value: 'CUSTOM',         label: 'Blank board',          description: 'Empty board you can shape from scratch.' },
  { value: 'JOB',            label: 'Project / Job board',  description: 'Per-project workspace with Drawings, RFI, Site, Progress lanes.' },
  { value: 'JOBS_PIPELINE',  label: 'Jobs pipeline',        description: 'Org-wide pipeline of every active job.' },
  { value: 'QUOTE_PIPELINE', label: 'Quote pipeline',       description: 'Open → Quoting → Submitted → Awarded / Lost.' },
  { value: 'AR_AP',          label: 'Cashflow (AR/AP)',     description: 'Bills + invoices side-by-side with cash-flow rollup.' },
  { value: 'INBOX',          label: 'Personal inbox',       description: 'Your private triage lane for unsorted emails.' },
  // FEAT-2026-06-09-KANBAN-CONSTRUCTION-KINDS — first-class construction
  // department kinds (replaces the placeholder quick-starts that mapped to
  // CUSTOM/TEAM). Backend BoardKind + canonical lane sets ship in the
  // companion MR; the server allows operator creation of all of these.
  { value: 'PROCUREMENT',    label: 'Procurement',          description: 'Purchase orders & material buys: Requested → Ordered → Delivered → Invoiced.' },
  { value: 'SUBCONTRACTORS', label: 'Subcontractors',       description: 'Subbie engagement & compliance: Prospect → Engaged → On site → Complete.' },
  { value: 'SAFETY',         label: 'Safety & WHS',         description: 'Incidents, hazards, SWMS & inductions: Reported → Investigating → Closed.' },
  { value: 'QUALITY',        label: 'Quality & Defects',    description: 'Defects & handover snags: Reported → In progress → Re-inspect → Closed.' },
  { value: 'PLANT',          label: 'Plant & Equipment',    description: 'Plant register & maintenance: Available → In use → Maintenance → Off-hire.' },
  { value: 'DOCUMENTS',      label: 'Documents & RFIs',     description: 'RFIs, submittals & doc control: Requested → Submitted → Approved.' },
  { value: 'HR',             label: 'HR & Recruitment',     description: 'Hiring pipeline: Applicants → Screening → Interview → Offer → Hired.' },
  { value: 'TENDERS',        label: 'Tenders & Bids',       description: 'Pre-CRM bid funnel: Identified → Bidding → Submitted → Won / Lost.' },
]

// FEAT-2026-06-07-KANBAN-CONSTRUCTION — construction quick-starts.
// Operator (2026-06-07): "first principle what is missing in the boards
// for a construction company". A builder's delivery + compliance workflow
// needs Procurement/POs, a Subcontractor register, Safety & Compliance,
// Defects/Handover, an RFI register and more.
//
// FEAT-2026-06-09-KANBAN-CONSTRUCTION-KINDS — these now map to FIRST-CLASS
// BoardKinds (backend companion MR) with canonical lane sets, instead of the
// previous placeholder CUSTOM/TEAM. A quick-start now just pre-fills the
// name; the real kind brings its own lanes on create.
export const CONSTRUCTION_QUICK_STARTS: Array<{
  id: string
  label: string
  name: string
  kind: BoardKind
  description: string
}> = [
  { id: 'procurement',    label: 'Procurement / POs',      name: 'Procurement',       kind: 'PROCUREMENT',    description: 'Purchase orders & material buys — Requested → Ordered → Delivered → Invoiced.' },
  { id: 'subcontractors', label: 'Subcontractor register', name: 'Subcontractors',    kind: 'SUBCONTRACTORS', description: 'Subbie engagement & compliance — insurances, SWMS, retentions, scope.' },
  { id: 'safety',         label: 'Safety & WHS',           name: 'Safety & WHS',      kind: 'SAFETY',         description: 'SWMS, inductions, incidents & toolbox talks.' },
  { id: 'quality',        label: 'Quality & defects',      name: 'Quality & Defects', kind: 'QUALITY',        description: 'Snagging, PC & defects-liability — Reported → In progress → Closed.' },
  { id: 'plant',          label: 'Plant & equipment',      name: 'Plant & Equipment', kind: 'PLANT',          description: 'Plant register, hire & maintenance — Available → In use → Maintenance.' },
  { id: 'documents',      label: 'Documents & RFIs',       name: 'Documents & RFIs',  kind: 'DOCUMENTS',      description: 'RFIs, submittals & doc control — Requested → Submitted → Approved.' },
  { id: 'hr',             label: 'HR & recruitment',       name: 'HR & Recruitment',  kind: 'HR',             description: 'Hiring pipeline — Applicants → Screening → Interview → Offer → Hired.' },
  { id: 'tenders',        label: 'Tenders & bids',         name: 'Tenders & Bids',    kind: 'TENDERS',        description: 'Pre-CRM bid funnel — Identified → Bidding → Submitted → Won / Lost.' },
]

export const VISIBILITY_LABEL: Record<BoardVisibility, string> = {
  PRIVATE:        'Private — just me',
  BOARD_MEMBERS:  'Members only — invite specific people',
  ORG:            'Workspace — everyone in the org',
}

/**
 * Convenience helper: turn a `CreateBoardInput` into the snake_case
 * `BoardCreate` shape the API expects, including a derived slug.
 * Tenants typically pass this straight to `api.createBoard`.
 */
export function buildBoardCreatePayload(input: CreateBoardInput): {
  organisation: number
  kind: BoardKind
  name: string
  slug: string
  description: string
  visibility: BoardVisibility
} {
  return {
    organisation: input.organisation,
    kind: input.kind,
    name: input.name,
    slug: buildUniqueBoardSlug(input.name),
    description: input.description ?? '',
    visibility: input.visibility,
  }
}
